import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useMemo, useState } from "react";
import SlidingTabs from "@/components/ui/sliding-tabs";
import { Link } from "react-router-dom";
import {
    listInvestorProperties,
    updateInvestorProperty,
    searchInvestorReports,
    investorComparisonCsvUrl,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature } from "@/lib/tier";
import LockedFeatureCard from "@/components/investor/LockedFeatureCard";
import QuoteTrackingPanel from "@/components/investor/QuoteTrackingPanel";
import ExposureActualsPanel from "@/components/investor/ExposureActualsPanel";
import PlusFeaturesPanel from "@/components/investor/PlusFeaturesPanel";
import {
    Search,
    Filter,
    Download,
    ArrowUpRight,
    MapPin,
    AlertTriangle,
    Shield,
    Droplets,
    DollarSign,
    Layers,
} from "lucide-react";
import { toast } from "sonner";

const FILTER_CHIPS = [
    { id: "all", label: "All properties" },
    { id: "high_repair_exposure", label: "High repair exposure", icon: DollarSign },
    { id: "strong_leverage", label: "Strong leverage", icon: Layers },
    { id: "structural_concerns", label: "Structural", icon: AlertTriangle },
    { id: "water_concerns", label: "Water/moisture", icon: Droplets },
    { id: "financing_flags", label: "Financing flags", icon: Shield },
    { id: "high_priority_issues", label: "2+ high-priority", icon: AlertTriangle },
    { id: "pipeline_under_contract", label: "Under contract" },
    { id: "pipeline_inspection_period", label: "Inspection period" },
    { id: "pipeline_negotiating", label: "Negotiating" },
    { id: "missing_zip", label: "Missing ZIP", icon: MapPin },
];

function matchRow(row, filterId) {
    switch (filterId) {
        case "all":
            return true;
        case "high_repair_exposure":
            return (row.total_repair_exposure_high || 0) >= 25000;
        case "strong_leverage":
            return row.negotiation_leverage === "strong";
        case "structural_concerns":
            return (row.structural_concern_count || 0) > 0;
        case "water_concerns":
            return (row.water_concern_count || 0) > 0;
        case "financing_flags":
            return !!row.has_financing_insurance_flags;
        case "high_priority_issues":
            return (row.high_priority_issue_count || 0) >= 2;
        case "pipeline_under_contract":
            return row.pipeline_status === "under_contract";
        case "pipeline_inspection_period":
            return row.pipeline_status === "inspection_period";
        case "pipeline_negotiating":
            return row.pipeline_status === "negotiating";
        case "missing_zip":
            return !row.zip_code;
        default:
            return false;
    }
}

function formatUsdRange(low, high) {
    if (!low && !high) return "—";
    const fmt = (n) =>
        n >= 1000
            ? `$${Math.round(n / 1000)}k`
            : `$${n}`;
    return `${fmt(low || 0)} – ${fmt(high || 0)}`;
}

function ScorePill({ score, band }) {
    if (score === null || score === undefined) return <span className="text-bl-muted">—</span>;
    const tone =
        score >= 70
            ? "bg-[#C85B3A]/15 text-[#C85B3A]"
            : score >= 40
              ? "bg-[#D4A62A]/15 text-[#A6831A]"
              : "bg-bl-teal-tint text-bl-primary";
    return (
        <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono-bl ${tone}`}
            title={band || ""}
        >
            {score}
        </span>
    );
}

function PipelineSelect({ row, statuses, labels, onChange }) {
    const [pending, setPending] = useState(false);
    return (
        <select
            value={row.pipeline_status}
            disabled={pending}
            onChange={async (e) => {
                const next = e.target.value;
                setPending(true);
                try {
                    await onChange(row.property_id, next);
                } finally {
                    setPending(false);
                }
            }}
            className="text-xs px-2 py-1 rounded-md border border-bl bg-bl-surface text-bl"
            data-testid={`comparison-pipeline-${row.property_id}`}
            aria-label={`Pipeline status for ${row.property_name}`}
        >
            {statuses.map((s) => (
                <option key={s} value={s}>
                    {labels[s] || s}
                </option>
            ))}
        </select>
    );
}

export default function InvestorComparison() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState({
        properties: [],
        tier: null,
        pipeline_statuses: [],
        pipeline_labels: {},
    });
    const [loading, setLoading] = useState(true);
    const [filterId, setFilterId] = useState("all");
    const [searchQ, setSearchQ] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [tab, setTab] = useState("compare"); // compare | quotes | actuals

    const canAccess = hasFeature(user, "deal_comparison_dashboard");

    useEffect(() => {
        let cancelled = false;
        if (!canAccess || authLoading) return undefined;
        (async () => {
            try {
                const r = await listInvestorProperties();
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail?.message || "Could not load comparison.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canAccess, authLoading]);

    const filteredRows = useMemo(() => {
        const rows = data.properties || [];
        if (filterId === "all") return rows;
        return rows.filter((r) => matchRow(r, filterId));
    }, [data, filterId]);

    const chipCounts = useMemo(() => {
        const counts = { all: data.properties.length };
        for (const chip of FILTER_CHIPS) {
            if (chip.id === "all") continue;
            counts[chip.id] = data.properties.filter((r) => matchRow(r, chip.id)).length;
        }
        return counts;
    }, [data]);

    const updateStatus = async (propertyId, nextStatus) => {
        try {
            const updated = await updateInvestorProperty(propertyId, {
                pipeline_status: nextStatus,
            });
            setData((d) => ({
                ...d,
                properties: d.properties.map((p) =>
                    p.property_id === propertyId
                        ? {
                              ...p,
                              pipeline_status: updated.pipeline_status,
                              pipeline_status_label: data.pipeline_labels[updated.pipeline_status],
                          }
                        : p,
                ),
            }));
            toast.success("Pipeline status updated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not update.");
        }
    };

    const runSearch = async (q) => {
        const trimmed = q.trim();
        if (!trimmed) {
            setSearchResults(null);
            return;
        }
        setSearching(true);
        try {
            const r = await searchInvestorReports(trimmed);
            setSearchResults(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Search failed.");
        } finally {
            setSearching(false);
        }
    };

    if (authLoading) {
        return (
            <PublicLayout>
                <section className="bl-container py-20 text-center">
                    <div className="bl-spinner mx-auto" />
                </section>
            </PublicLayout>
        );
    }

    if (!canAccess) {
        return (
            <PublicLayout>
                <section className="bl-container pt-14 pb-20">
                    <div className="bl-eyebrow">Deal comparison</div>
                    <h1 className="mt-4 font-display font-semibold text-3xl md:text-4xl text-bl">
                        Compare every property in one view.
                    </h1>
                    <p className="mt-4 text-bl-muted max-w-2xl leading-relaxed">
                        Side-by-side repair exposure, negotiation leverage, pipeline
                        status, and notes for every property you've analyzed.
                        Available on Investor Pro and above.
                    </p>
                    <div className="mt-8 max-w-2xl">
                        <LockedFeatureCard
                            feature="deal_comparison_dashboard"
                            title="Deal Comparison Dashboard"
                            description="Compare repair exposure, leverage, and pipeline status across every property — without re-opening each report. Upgrade to Pro to unlock."
                        />
                    </div>
                </section>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-paper" data-reveal="slide-left-lg">
                <div className="bl-container pt-14 pb-8" data-testid="investor-comparison-page">
                    <div className="bl-eyebrow">Investor workflow</div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight text-bl leading-tight">
                        Deal{" "}
                        <span className="font-italic-accent text-bl-primary">
                            Comparison
                        </span>
                        .
                    </h1>
                    <p className="mt-4 text-bl-muted max-w-2xl">
                        Side-by-side risk, exposure, leverage, and pipeline
                        across every property you've analyzed. Built entirely
                        from your stored report data — instant, no surprises.
                    </p>
                </div>
            </section>

            <section className="bl-container pb-16" data-reveal>
                {/* Tab bar — sliding underline indicator */}
                <SlidingTabs
                    className="mb-5"
                    active={tab}
                    onChange={setTab}
                    variant="underline"
                    testId="investor-tab"
                    ariaLabel="Investor dashboard sections"
                    tabs={[
                        { id: "compare", label: "Deal comparison" },
                        { id: "quotes", label: "Quote tracking" },
                        { id: "actuals", label: "Calibration" },
                        { id: "plus", label: "Plus workflow" },
                    ]}
                />

                {tab === "quotes" && <QuoteTrackingPanel />}
                {tab === "actuals" && <ExposureActualsPanel />}
                {tab === "plus" && <PlusFeaturesPanel />}
                {tab === "compare" && (<>
                {/* Controls */}
                <div className="bl-card p-4 md:p-5 mb-4 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-bl-muted" />
                        <input
                            type="search"
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") runSearch(searchQ);
                            }}
                            placeholder="Search across all reports (e.g. foundation, water, electrical)"
                            className="w-full pl-9 pr-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                            data-testid="comparison-search-input"
                        />
                    </div>
                    <button
                        onClick={() => runSearch(searchQ)}
                        disabled={!searchQ.trim() || searching}
                        className="bl-btn-secondary text-sm"
                        data-testid="comparison-search-btn"
                    >
                        {searching ? "Searching…" : "Search"}
                    </button>
                    <a
                        href={investorComparisonCsvUrl()}
                        className="bl-btn-secondary text-sm"
                        data-testid="comparison-csv-export"
                    >
                        <Download className="w-4 h-4" /> Export CSV
                    </a>
                </div>

                {/* Filter chips */}
                <div className="flex flex-wrap gap-2 mb-5" data-testid="comparison-filter-chips">
                    {FILTER_CHIPS.map((chip) => {
                        const Icon = chip.icon;
                        const count = chipCounts[chip.id] ?? 0;
                        const isActive = filterId === chip.id;
                        if (count === 0 && chip.id !== "all") return null;
                        return (
                            <button
                                key={chip.id}
                                onClick={() => setFilterId(chip.id)}
                                className={
                                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors " +
                                    (isActive
                                        ? "bg-bl-primary text-bl-surface"
                                        : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                                }
                                data-testid={`comparison-filter-${chip.id}`}
                            >
                                {Icon && <Icon className="w-3 h-3" />}
                                {chip.label}
                                <span className="text-[10px] opacity-80">·{count}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Search results inline */}
                {searchResults && (
                    <div
                        className="bl-card p-5 mb-5 bg-bl-teal-tint/20"
                        data-testid="comparison-search-results"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm font-medium text-bl">
                                {searchResults.result_count} report
                                {searchResults.result_count === 1 ? "" : "s"} mention{" "}
                                <span className="font-mono-bl">"{searchResults.query}"</span>
                            </div>
                            <button
                                onClick={() => {
                                    setSearchResults(null);
                                    setSearchQ("");
                                }}
                                className="text-xs text-bl-muted hover:text-bl"
                            >
                                Clear
                            </button>
                        </div>
                        {searchResults.results.length === 0 ? (
                            <p className="text-sm text-bl-muted">
                                No matches across your reports.
                            </p>
                        ) : (
                            <ul className="space-y-2 text-sm">
                                {searchResults.results.map((r) => (
                                    <li key={r.report_id} className="flex items-start gap-2">
                                        <Link
                                            to={`/report/${r.report_id}`}
                                            className="text-bl-primary font-medium hover:underline"
                                            data-testid={`comparison-search-hit-${r.report_id}`}
                                        >
                                            {r.property_address}
                                        </Link>
                                        <span className="text-bl-muted text-xs">
                                            · {r.match_count} mention
                                            {r.match_count === 1 ? "" : "s"}
                                        </span>
                                        {r.snippets[0] && (
                                            <span className="text-bl-muted italic flex-1 truncate">
                                                — {r.snippets[0]}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {/* Table */}
                {loading ? (
                    <div className="bl-card p-10 text-center text-bl-muted">
                        <div className="bl-spinner mx-auto mb-3" />
                        Loading comparison…
                    </div>
                ) : filteredRows.length === 0 ? (
                    <div className="bl-card p-10 text-center" data-testid="comparison-empty">
                        <h3 className="font-display font-semibold text-xl text-bl">
                            No properties to compare yet.
                        </h3>
                        <p className="mt-3 text-bl-muted leading-relaxed max-w-md mx-auto">
                            Once your investor reports finish generating, they'll
                            appear here automatically.
                        </p>
                        <Link to="/upload" className="bl-btn-primary mt-6 inline-flex">
                            Upload inspection <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div
                        className="bl-card overflow-x-auto"
                        data-testid="comparison-table"
                    >
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-bl">
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Property
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        ZIP
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Risk
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Leverage
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Exposure
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        High-priority
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Flags
                                    </th>
                                    <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">
                                        Pipeline
                                    </th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row) => (
                                    <tr
                                        key={row.property_id}
                                        className="border-b border-bl last:border-0 hover:bg-bl-bg/40"
                                        data-testid={`comparison-row-${row.property_id}`}
                                    >
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/report/${row.report_id}`}
                                                className="font-display font-medium text-bl hover:text-bl-primary"
                                            >
                                                {row.property_name}
                                            </Link>
                                            {row.notes_count > 0 && (
                                                <div className="mt-0.5 text-[10px] text-bl-muted">
                                                    {row.notes_count} note
                                                    {row.notes_count === 1 ? "" : "s"}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-mono-bl text-xs text-bl-muted">
                                            {row.zip_code || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <ScorePill
                                                score={row.inspection_risk_score}
                                                band={row.inspection_risk_band}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {row.negotiation_leverage ? (
                                                <span
                                                    className={
                                                        row.negotiation_leverage === "strong"
                                                            ? "text-bl-primary font-medium"
                                                            : row.negotiation_leverage === "weak"
                                                              ? "text-bl-muted"
                                                              : "text-bl"
                                                    }
                                                >
                                                    {row.negotiation_leverage}
                                                </span>
                                            ) : (
                                                <span className="text-bl-muted">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs font-mono-bl">
                                            {formatUsdRange(
                                                row.total_repair_exposure_low,
                                                row.total_repair_exposure_high,
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center text-xs font-mono-bl">
                                            {row.high_priority_issue_count}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            <div className="flex flex-wrap gap-1">
                                                {row.structural_concern_count > 0 && (
                                                    <span
                                                        className="px-1.5 py-0.5 rounded bg-[#C85B3A]/10 text-[#C85B3A]"
                                                        title="Structural concerns"
                                                    >
                                                        S
                                                    </span>
                                                )}
                                                {row.water_concern_count > 0 && (
                                                    <span
                                                        className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                                                        title="Water/moisture concerns"
                                                    >
                                                        W
                                                    </span>
                                                )}
                                                {row.has_financing_insurance_flags && (
                                                    <span
                                                        className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                                                        title="Financing/insurance flag"
                                                    >
                                                        $
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <PipelineSelect
                                                row={row}
                                                statuses={data.pipeline_statuses}
                                                labels={data.pipeline_labels}
                                                onChange={updateStatus}
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                to={`/report/${row.report_id}`}
                                                className="inline-flex items-center text-xs text-bl-primary hover:underline"
                                                data-testid={`comparison-open-${row.property_id}`}
                                            >
                                                Open <ArrowUpRight className="w-3 h-3 ml-0.5" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <p className="mt-5 text-xs text-bl-muted leading-relaxed">
                    <Filter className="w-3 h-3 inline mr-1 -mt-0.5" />
                    Filters, search, pipeline, and exports run entirely on
                    your stored report data — instant, never re-charged.
                </p>
                </>)}
            </section>
        </PublicLayout>
    );
}
